import styled from '@emotion/styled';
import { Laptop, PhoneAndroid, Wifi } from '@mui/icons-material';
import React, { useEffect } from 'react';
import { MenuButton, MenuContainer } from '~/modules/Elements/Menu';
import Modal from '~/modules/Elements/Modal';
import storage from '~/modules/utils/storage';

interface Props {
  onClose?: () => void;
}

const CONFIRM_WIFI_KEY = 'confirmed-wifi';

type NavigatorConnectionWithWifi = EventTarget & {
  type?: string;
};

export default function ConfirmWifiModal({ onClose }: Props) {
  const [closed, setClosed] = React.useState(storage.session.getItem(CONFIRM_WIFI_KEY) ?? false);

  const closeModal = () => {
    storage.session.setItem(CONFIRM_WIFI_KEY, true);
    setClosed(true);
    onClose?.();
  };

  useEffect(() => {
    const connection = navigator?.connection as NavigatorConnectionWithWifi | undefined;

    if (connection) {
      if (connection.type === 'wifi') {
        closeModal();
      } else {
        const onChange = () => {
          if (connection.type === 'wifi') {
            closeModal();
          }
        };
        connection.addEventListener?.('change', onChange);

        return () => connection.removeEventListener?.('change', onChange);
      }
    }
  }, []);

  return (
    <Modal onClose={closeModal} open={!closed}>
      <Menu>
        <Wrapper>
          <h3>
            Connect to the <strong>same Wi-Fi</strong>
          </h3>
          <IconsWrapper>
            <PhoneAndroid />
            ...
            <Wifi />
            ...
            <Laptop />
          </IconsWrapper>

          <h4>Connect to the same Wi-Fi as the game.</h4>
        </Wrapper>
        <h6>Otherwise microphones might not work properly.</h6>
        <MenuButton onClick={closeModal} focused data-test="confirm-wifi-connection">
          I&#39;m connected, continue
        </MenuButton>
      </Menu>
    </Modal>
  );
}

const Menu = styled(MenuContainer)`
  gap: 12px;
  color: var(--walky-ink);
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;

  h3,
  h4 {
    margin: 0;
  }

  h3 {
    font-size: 20px;
    line-height: 1.15;
  }

  h4 {
    color: var(--walky-muted);
    font-size: 14px;
    line-height: 1.3;
  }
`;

const IconsWrapper = styled.div`
  margin: 8px 0;
  display: flex;
  color: var(--walky-ink);
  align-items: center;
  justify-content: center;
  gap: 10px;

  @keyframes pulse {
    0% {
      color: var(--walky-muted);
      transform: scale(1);
    }
    25% {
      color: var(--walky-orange);
      transform: scale(1.08);
    }
    50% {
      color: var(--walky-muted);
      transform: scale(1);
    }
  }

  svg {
    animation: pulse 2s infinite;
    font-size: 42px;

    :nth-of-type(1) {
      animation-delay: 0s;
    }
    :nth-of-type(2) {
      animation-delay: 0.5s;
    }
    :nth-of-type(3) {
      animation-delay: 1s;
    }
  }
`;
